(() => {
  const USE_CASES = {
    homeBackup: {
      label: 'Home Backup',
      description: 'Prioritizes actual backup strength: capacity, single unit AC output, solar input, expansion, 240V capability, cycle life, recharge speed, and UPS support. Portability is intentionally not scored here.',
      weights: { capacity: 28, acOutput: 18, solarInput: 17, expansion: 15, support240v: 10, cycleLife: 7, acRecharge: 3, ups: 2 }
    },
    rv: {
      label: 'RV Value',
      description: 'Prioritizes serious RV backup strength: capacity, RV friendly output, solar input, expansion, cycle life, and recharge speed. Weight matters, but it does not outweigh the power system.',
      weights: { capacity: 24, solarInput: 18, acOutput: 16, expansion: 12, rvSupport: 10, cycleLife: 8, acRecharge: 6, support240v: 4, portability: 2 }
    },
    camping: {
      label: 'Camping Value',
      description: 'Prioritizes portability first. Large home backup class units can still score for power, but lighter stations should win for true camping use.',
      weights: { portability: 36, capacity: 12, solarInput: 12, ports: 11, acOutput: 9, acRecharge: 7, cycleLife: 6, quietFit: 5, ups: 2 }
    },
    emergency: {
      label: 'Emergency Essentials',
      description: 'Prioritizes actual outage readiness: capacity, lifetime energy, output, solar recovery, expansion, cycle life, UPS support, and recharge speed.',
      weights: { capacity: 30, lifetimeValue: 16, acOutput: 14, solarInput: 14, expansion: 10, cycleLife: 8, support240v: 4, ups: 3, acRecharge: 1 }
    },
    offGridSolar: {
      label: 'Off Grid Solar',
      description: 'Prioritizes solar input, storage capacity, expansion, cycle life, output, and 240V capability. Price per Wh does not override actual system strength here.',
      weights: { solarInput: 30, capacity: 24, expansion: 20, cycleLife: 12, acOutput: 7, support240v: 5, acRecharge: 2 }
    },
    worksite: {
      label: 'Worksite Power',
      description: 'Prioritizes hard working output: AC power, backup capacity, solar input, surge capability, expansion, 240V support, and recharge speed.',
      weights: { acOutput: 24, capacity: 22, solarInput: 14, surgeOutput: 11, expansion: 11, support240v: 8, cycleLife: 5, ports: 3, acRecharge: 2 }
    }
  };

  const PRIORITY_ROWS = [
    'model sku', 'capacity', 'battery chemistry', 'cycle life', 'warranty', 'weight', 'dimensions',
    'charge temperature', 'discharge temperature', 'storage ambient temperature',
    'ac output', '120v ac output', '240v ac output', 'dc output', 'usb c output', 'usb a output', 'cigar port', 'dc 5525 output',
    'ac input', 'solar input', 'car input', 'ups power', 'extra battery', 'charge time', 'note'
  ];

  const LABELS = {
    'model sku': 'Model SKU',
    capacity: 'Capacity',
    'battery chemistry': 'Battery Chemistry',
    'cycle life': 'Cycle Life',
    warranty: 'Warranty',
    weight: 'Weight',
    dimensions: 'Dimensions',
    'charge temperature': 'Charge Temperature',
    'discharge temperature': 'Discharge Temperature',
    'storage ambient temperature': 'Storage Ambient Temperature',
    'ac output': 'AC Output',
    '120v ac output': '120V AC Output',
    '240v ac output': '240V AC Output',
    'dc output': 'DC Output',
    'usb c output': 'USB C Output',
    'usb a output': 'USB A Output',
    'cigar port': 'Cigar Port',
    'dc 5525 output': 'DC 5525 Output',
    'ac input': 'AC Input',
    'solar input': 'Solar Input',
    'car input': 'Car Input',
    'ups power': 'UPS Power',
    'extra battery': 'Extra Battery',
    'charge time': 'Charging Time',
    note: 'Note'
  };

  function init(event) {
    const scope = event && event.target ? event.target : document;
    const roots = scope.matches && scope.matches('.ggcp[id^="GGComparePro-"]')
      ? [scope]
      : Array.from(scope.querySelectorAll('.ggcp[id^="GGComparePro-"]'));

    roots.forEach((root) => {
      if (!root || root.dataset.ready === 'true') return;
      root.dataset.ready = 'true';

      const dataNode = root.querySelector('script[type="application/json"][id^="GGCompareData-"]');
      const input = root.querySelector('[data-gg-search]');
      const resultsEl = root.querySelector('[data-gg-results]');
      const selectedEl = root.querySelector('[data-gg-selected]');
      const trayEl = root.querySelector('[data-gg-tray]');
      const tableEl = root.querySelector('[data-gg-table]');
      const emptyEl = root.querySelector('[data-gg-empty]');
      const chipsEl = root.querySelector('[data-gg-chips]');
      const messageEl = root.querySelector('[data-gg-message]');
      const statusEl = root.querySelector('[data-gg-status]');
      const clearButtons = root.querySelectorAll('[data-gg-clear]');
      const trayTitle = root.querySelector('[data-gg-tray-title]');
      const traySubtitle = root.querySelector('[data-gg-tray-subtitle]');
      const pickerToggleButton = root.querySelector('[data-gg-toggle-picker]');
      const useCasePanelEl = root.querySelector('[data-gg-usecase-panel]');
      const useCaseTabsEl = root.querySelector('[data-gg-usecase-tabs]');
      const valueCardsEl = root.querySelector('[data-gg-value-cards]');
      const useCaseTitleEl = root.querySelector('[data-gg-usecase-title]');
      const useCaseDescriptionEl = root.querySelector('[data-gg-usecase-description]');
      const maxCompare = Number(root.dataset.maxCompare || 4);

      if (!dataNode || !input || !resultsEl || !selectedEl || !tableEl || !emptyEl || !chipsEl || !messageEl) return;

      let products = [];
      let selected = [];
      let pickerOpen = true;
      let mobileSpecsExpanded = false;
      let activeGroup = 'all';
      let activeUseCase = 'homeBackup';
      let jsonLoadFailed = false;
      let suppressUrlUpdate = false;

      try {
        products = JSON.parse((dataNode.textContent || '[]').trim() || '[]');
      } catch (error) {
        products = [];
        jsonLoadFailed = true;
      }

      const productMap = new Map();
      products.forEach((product) => {
        const id = String(product && product.id ? product.id : '');
        if (!id || productMap.has(id)) return;
        const normalizedProduct = {
          ...product,
          id,
          specs: normalizeSpecs(product.specs),
          group: inferGroup(product),
          shortTitle: getShortModelTitle(product)
        };
        normalizedProduct.search = normalize([
          normalizedProduct.title,
          normalizedProduct.vendor,
          normalizedProduct.type,
          normalizedProduct.group,
          normalizedProduct.collectionTitle,
          normalizedProduct.sku,
          normalizedProduct.keywords,
          ...(normalizedProduct.variantSkus || []),
          Object.keys(normalizedProduct.specs || {}).join(' '),
          Object.values(normalizedProduct.specs || {}).map(formatSpecValue).join(' ')
        ].filter(Boolean).join(' '));
        productMap.set(id, normalizedProduct);
      });
      products = Array.from(productMap.values());

      function normalize(value) {
        return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
      }

      function escapeHTML(value) {
        return String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }

      function stripHTML(value) {
        return String(value ?? '')
          .replace(/<br\s*\/?\s*>/gi, ' ')
          .replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }

      function isPlainObject(value) {
        return value && typeof value === 'object' && !Array.isArray(value);
      }

      function isBlankSpecValue(value) {
        if (value === null || value === undefined) return true;
        if (Array.isArray(value)) return value.length === 0 || value.every(isBlankSpecValue);
        if (isPlainObject(value)) return Object.keys(value).length === 0;
        return String(value).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim() === '';
      }

      function addSpecValue(target, key, value) {
        const cleanKey = stripHTML(key).replace(/[:\-–—]+$/g, '').replace(/\s+/g, ' ').trim();
        if (!cleanKey || isBlankSpecValue(value)) return;
        if (/^(id|handle|type|url|image|title|created_at|updated_at)$/i.test(cleanKey)) return;
        target[cleanKey] = value;
      }

      function richTextToLines(node) {
        const lines = [];
        function walk(item) {
          if (!item) return;
          if (typeof item === 'string') {
            if (item.trim()) lines.push(item.trim());
            return;
          }
          if (Array.isArray(item)) {
            item.forEach(walk);
            return;
          }
          if (!isPlainObject(item)) return;
          if (typeof item.value === 'string') lines.push(item.value.trim());
          if (typeof item.text === 'string') lines.push(item.text.trim());
          if (Array.isArray(item.children)) item.children.forEach(walk);
        }
        walk(node);
        return lines.filter(Boolean);
      }

      function parseSpecLine(line, target) {
        const cleanLine = stripHTML(line);
        if (!cleanLine) return;
        const match = cleanLine.match(/^([^:|=–—-]{2,90})\s*(?::|\||=|–|—|-|\t){1}\s*(.+)$/);
        if (match) addSpecValue(target, match[1], match[2]);
      }

      function flattenObjectSpecs(object, target, sectionLabel = '') {
        Object.entries(object || {}).forEach(([key, value]) => {
          if (isBlankSpecValue(value)) return;
          if (/^(specifications|specs|items|children|fields)$/i.test(key)) {
            Object.assign(target, normalizeSpecs(value));
            return;
          }

          if (isPlainObject(value)) {
            const label = value.label || value.name || value.title || value.key || value.spec || value.specification || value.heading;
            const content = value.value || value.content || value.text || value.answer || value.description;
            if (label && !isBlankSpecValue(content)) {
              addSpecValue(target, label, content);
              return;
            }
            flattenObjectSpecs(value, target, key);
            return;
          }

          if (Array.isArray(value)) {
            if (value.every((item) => typeof item === 'string')) {
              addSpecValue(target, key, value.join(', '));
              return;
            }
            Object.assign(target, normalizeSpecs(value));
            return;
          }

          addSpecValue(target, key, value);
        });
      }

      function normalizeSpecs(specs) {
        const normalized = {};
        if (!specs) return normalized;

        if (typeof specs === 'string') {
          const raw = specs.trim();
          if (!raw) return normalized;
          try {
            return normalizeSpecs(JSON.parse(raw));
          } catch (error) {
            raw.split(/\r?\n/).forEach((line) => parseSpecLine(line, normalized));
            return normalized;
          }
        }

        if (Array.isArray(specs)) {
          specs.forEach((item) => {
            if (isBlankSpecValue(item)) return;
            if (typeof item === 'string') {
              parseSpecLine(item, normalized);
              return;
            }
            if (Array.isArray(item)) {
              if (item.length >= 2) addSpecValue(normalized, item[0], item.slice(1).join(', '));
              return;
            }
            if (isPlainObject(item)) {
              const label = item.label || item.name || item.title || item.key || item.spec || item.specification || item.heading;
              const value = item.value || item.content || item.text || item.answer || item.description;
              if (label && !isBlankSpecValue(value)) {
                addSpecValue(normalized, label, value);
                return;
              }
              flattenObjectSpecs(item, normalized);
            }
          });
          return normalized;
        }

        if (isPlainObject(specs)) {
          if (specs.type === 'root' && Array.isArray(specs.children)) {
            richTextToLines(specs.children).forEach((line) => parseSpecLine(line, normalized));
          }
          flattenObjectSpecs(specs, normalized);
        }

        return normalized;
      }

      function formatSpecValue(value) {
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return value.map(formatSpecValue).filter(Boolean).join(', ');
        if (isPlainObject(value)) return Object.entries(value).map(([key, val]) => `${key}: ${formatSpecValue(val)}`).filter(Boolean).join('; ');
        return String(value);
      }

      function formatSpecHTML(value) {
        if (isBlankSpecValue(value)) return '<span class="ggcp-fit-note">Not listed</span>';
        return escapeHTML(formatSpecValue(value))
          .replace(/&lt;br\s*\/?\s*&gt;/gi, '<br>')
          .replace(/;\s*/g, ';<br>');
      }

      function canonicalSpecKey(label) {
        const normalized = normalize(label)
          .replace(/usb c/g, 'usb c')
          .replace(/usb a/g, 'usb a')
          .trim();

        const aliases = {
          'model sku': 'model sku',
          sku: 'model sku',
          'battery capacity': 'capacity',
          'rated capacity': 'capacity',
          'nominal capacity': 'capacity',
          capacity: 'capacity',
          'cell chemistry': 'battery chemistry',
          chemistry: 'battery chemistry',
          'battery chemistry': 'battery chemistry',
          'battery type': 'battery chemistry',
          'cycle life': 'cycle life',
          cycles: 'cycle life',
          warranty: 'warranty',
          'warranty period': 'warranty',
          weight: 'weight',
          'net weight': 'weight',
          dimensions: 'dimensions',
          dimension: 'dimensions',
          'charge temperature': 'charge temperature',
          'charging temperature': 'charge temperature',
          'discharge temperature': 'discharge temperature',
          'storage ambient temperature': 'storage ambient temperature',
          'storage temperature': 'storage ambient temperature',
          'ac output': 'ac output',
          '120v ac output': '120v ac output',
          '240v ac output': '240v ac output',
          'usb c output': 'usb c output',
          'usb a output': 'usb a output',
          'cigar port': 'cigar port',
          'dc 5525 output': 'dc 5525 output',
          'dc output': 'dc output',
          'dc xt60 output 5525': 'dc output',
          'ac input': 'ac input',
          'ac charging input': 'ac input',
          'solar input': 'solar input',
          'solar charging input': 'solar input',
          'pv input': 'solar input',
          'car input': 'car input',
          'ups power': 'ups power',
          ups: 'ups power',
          'extra battery': 'extra battery',
          expansion: 'extra battery',
          expandable: 'extra battery',
          'charging time': 'charge time',
          'charge time': 'charge time',
          'recharge time': 'charge time',
          note: 'note'
        };

        if (aliases[normalized]) return aliases[normalized];
        if (/\b240\s*v\b.*\bac\b.*\boutput\b|\b240\s*v\b.*\boutput\b/.test(normalized)) return '240v ac output';
        if (/\b120\s*v\b.*\bac\b.*\boutput\b|\b120\s*v\b.*\boutput\b/.test(normalized)) return '120v ac output';
        if (/\bac\b.*\boutput\b|\binverter\b.*\boutput\b/.test(normalized)) return 'ac output';
        if (/\bsolar\b.*\binput\b|\bpv\b.*\binput\b|\bxt60\b/.test(normalized)) return 'solar input';
        if (/\bac\b.*\binput\b|\bwall\b.*\binput\b|\bcharging\b.*\binput\b/.test(normalized)) return 'ac input';
        if (/\bcycle\b/.test(normalized)) return 'cycle life';
        if (/\bweight\b/.test(normalized)) return 'weight';
        if (/\bdimension|\bsize\b/.test(normalized)) return 'dimensions';
        if (/\bcapacity\b/.test(normalized)) return 'capacity';
        return normalized;
      }

      function chooseSpecLabel(labels, canonical) {
        const pretty = LABELS[canonical] || labels.find(Boolean) || canonical;
        return pretty;
      }

      function inferGroup(product) {
        const currentGroup = String(product.group || '').trim();
        if (currentGroup && currentGroup.toLowerCase() !== 'products') return currentGroup;
        const haystack = normalize([product.title, product.vendor, product.type, product.collectionTitle, product.sku, product.keywords, ...(product.variantSkus || [])].filter(Boolean).join(' '));
        if (/charge controller|mppt|pwm|controller/.test(haystack)) return 'Charge Controllers';
        if (/battery charger|dc dc charger|dc to dc|charger/.test(haystack)) return 'Battery Chargers';
        if (/solar connector|mc4|connector|adapter cable|extension cable|cable/.test(haystack)) return 'Solar Connectors & Cables';
        if (/solar panel|pv panel|panel|module|bifacial|monocrystalline|n type/.test(haystack)) return 'Solar Panels';
        if (/power station|solar generator|portable generator|generator/.test(haystack)) return 'Portable Power Stations';
        if (/inverter/.test(haystack)) return 'Inverters';
        if (/lifepo4|lithium battery|battery|batteries|agm/.test(haystack)) return 'Batteries';
        return product.type || product.collectionTitle || 'Products';
      }

      function getShortModelTitle(product) {
        const rawTitle = String((product && product.title) || '').replace(/\s+/g, ' ').trim();
        if (!rawTitle) return '';
        return rawTitle
          .replace(/\s*[|]+\s*.*$/g, '')
          .replace(/\bPortable Power Station\b/gi, 'Power Station')
          .replace(/\bSolar Generator\b/gi, 'Generator')
          .trim();
      }

      function titleHTML(product) {
        return escapeHTML(product.shortTitle || product.title || 'Product');
      }

      function imageHTML(product, className = 'ggcp-product-img') {
        if (product.image) return `<img class="${className}" src="${escapeHTML(product.image)}" alt="${escapeHTML(product.title || 'Product image')}" loading="lazy">`;
        return `<div class="ggcp-placeholder-img ${className}" aria-hidden="true">GG</div>`;
      }

      function setMessage(text = '', type = '') {
        messageEl.textContent = text;
        messageEl.classList.toggle('is-warning', type === 'warning');
      }

      function setStatus(title = 'Ready', text = 'Choose a category or search below.', type = '') {
        if (!statusEl) return;
        const strong = statusEl.querySelector('strong');
        const span = statusEl.querySelector('span:last-child');
        const dot = statusEl.querySelector('.ggcp-status__dot');
        if (strong) strong.textContent = title;
        if (span) span.textContent = text;
        if (dot) dot.style.background = type === 'warning' ? 'var(--ggcp-orange)' : 'var(--ggcp-gold)';
      }

      function preserveScroll(callback) {
        const x = window.scrollX || window.pageXOffset || 0;
        const y = window.scrollY || window.pageYOffset || 0;
        callback();
        window.requestAnimationFrame(() => window.scrollTo(x, y));
      }

      function getGroups() {
        return Array.from(new Set(products.map((product) => product.group).filter(Boolean))).sort((a, b) => a.localeCompare(b));
      }

      function scoreProduct(product, query, tokens) {
        if (!query) return 1;
        const title = normalize(product.title);
        const sku = normalize(product.sku);
        const vendor = normalize(product.vendor);
        const group = normalize(product.group);
        let score = 0;
        if (sku === query) score += 140;
        if ((product.variantSkus || []).map(normalize).includes(query)) score += 120;
        if (title === query) score += 100;
        if (sku.startsWith(query)) score += 80;
        if (title.startsWith(query)) score += 70;
        if (vendor.startsWith(query)) score += 45;
        if (group.includes(query)) score += 30;
        tokens.forEach((token) => {
          if (sku.includes(token)) score += 28;
          if (title.includes(token)) score += 24;
          if (vendor.includes(token)) score += 15;
          if (product.search.includes(token)) score += 8;
        });
        return score;
      }

      function getVisibleProducts() {
        const query = normalize(input.value);
        const tokens = query.split(' ').filter(Boolean);
        const lockedGroup = selected.length ? selected[0].group : null;
        const groupToUse = lockedGroup || activeGroup;
        return products
          .filter((product) => groupToUse === 'all' || product.group === groupToUse)
          .filter((product) => !tokens.length || tokens.every((token) => product.search.includes(token)))
          .map((product) => ({ product, score: scoreProduct(product, query, tokens) }))
          .sort((a, b) => b.score !== a.score ? b.score - a.score : normalize(a.product.title).localeCompare(normalize(b.product.title)))
          .map((item) => item.product)
          .slice(0, 8);
      }

      function renderChips() {
        const groups = getGroups();
        const lockedGroup = selected.length ? selected[0].group : null;
        const allActive = activeGroup === 'all' && !lockedGroup;
        chipsEl.innerHTML = [
          `<button class="ggcp-chip ${allActive ? 'is-active' : ''} ${lockedGroup ? 'is-locked' : ''}" type="button" data-group="all">All</button>`,
          ...groups.map((group) => {
            const isActive = lockedGroup ? group === lockedGroup : group === activeGroup;
            const isLocked = lockedGroup && group !== lockedGroup;
            return `<button class="ggcp-chip ${isActive ? 'is-active' : ''} ${isLocked ? 'is-locked' : ''}" type="button" data-group="${escapeHTML(group)}">${escapeHTML(group)}</button>`;
          })
        ].join('');
      }

      function renderResults() {
        const query = normalize(input.value);
        const matches = getVisibleProducts();
        const lockedGroup = selected.length ? selected[0].group : null;

        if (jsonLoadFailed) {
          resultsEl.innerHTML = '';
          setMessage('Product comparison data could not be read. Replace the section code and save again.', 'warning');
          setStatus('Needs attention', 'Comparison data could not load.', 'warning');
          return;
        }

        if (!products.length) {
          resultsEl.innerHTML = '';
          setMessage('No products are loaded yet. Add Comparable Collection blocks or turn on the All Products fallback setting.', 'warning');
          setStatus('No data', 'Add collections in the theme editor.', 'warning');
          return;
        }

        if (!matches.length) {
          resultsEl.innerHTML = '';
          setMessage('No matching products found. Try a shorter model number, SKU, brand, watts, or amp hours.', 'warning');
          setStatus('No match', 'Try a shorter search.', 'warning');
          return;
        }

        const message = lockedGroup
          ? `Locked to ${lockedGroup}. Remove selected products to switch categories.`
          : query
            ? `${matches.length} matching product${matches.length === 1 ? '' : 's'} found.`
            : activeGroup === 'all'
              ? `${products.length} products loaded. Start typing or choose a category.`
              : `Showing ${activeGroup}. Search within this category or add a product.`;

        setMessage(message);
        setStatus(selected.length ? `${selected.length}/${maxCompare} selected` : 'Ready', selected.length ? `Comparing ${selected[0].group}.` : 'Choose a category or search below.');

        resultsEl.innerHTML = matches.map((product) => {
          const alreadySelected = selected.some((item) => String(item.id) === String(product.id));
          const wrongGroup = selected.length && product.group !== selected[0].group;
          const atLimit = selected.length >= maxCompare && !alreadySelected;
          const disabled = alreadySelected || wrongGroup || atLimit;
          let buttonText = 'Add';
          if (alreadySelected) buttonText = 'Added';
          if (wrongGroup) buttonText = 'Locked';
          if (atLimit) buttonText = 'Full';
          return `
            <article class="ggcp-result">
              ${imageHTML(product)}
              <div class="ggcp-result__body">
                <h3>${titleHTML(product)}</h3>
                ${product.sku ? `<span class="ggcp-result__sku">SKU: ${escapeHTML(product.sku)}</span>` : '<span class="ggcp-result__sku">Model details available on product page</span>'}
                <div class="ggcp-result__category"><span class="ggcp-pill ggcp-pill--category">${escapeHTML(product.group)}</span></div>
                <div class="ggcp-result__bottom">
                  <span class="ggcp-price">${escapeHTML(product.price || 'View price')}</span>
                  <button class="ggcp-add" type="button" data-add="${escapeHTML(product.id)}" ${disabled ? 'disabled' : ''}>${buttonText}</button>
                </div>
              </div>
            </article>
          `;
        }).join('');
      }

      function getProductShareKey(product) {
        return encodeURIComponent(String((product && product.handle) || (product && product.id) || '').trim());
      }

      function findProductByShareKey(rawKey) {
        const decoded = decodeURIComponent(String(rawKey || '').trim());
        if (!decoded) return null;
        const normalizedKey = decoded.toLowerCase();
        return products.find((product) => String(product.handle || '').toLowerCase() === normalizedKey || String(product.id || '') === decoded) || null;
      }

      function updateShareUrl() {
        if (suppressUrlUpdate) return;
        const url = new URL(window.location.href);
        const keys = selected.map(getProductShareKey).filter(Boolean);
        if (keys.length) url.searchParams.set('compare', keys.join(','));
        else url.searchParams.delete('compare');
        window.history.replaceState({}, '', url.toString());
      }

      function restoreComparisonFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const raw = params.get('compare');
        if (!raw) return;
        const restored = [];
        let lockedGroup = '';
        raw.split(',').map((item) => item.trim()).filter(Boolean).forEach((key) => {
          if (restored.length >= maxCompare) return;
          const product = findProductByShareKey(key);
          if (!product) return;
          if (!lockedGroup) lockedGroup = product.group;
          if (product.group !== lockedGroup) return;
          if (restored.some((item) => String(item.id) === String(product.id))) return;
          restored.push(product);
        });
        if (!restored.length) return;
        suppressUrlUpdate = true;
        selected = restored;
        activeGroup = restored[0].group;
        pickerOpen = restored.length < 2;
        mobileSpecsExpanded = false;
        suppressUrlUpdate = false;
      }

      function addProduct(id) {
        const product = productMap.get(String(id));
        if (!product) return;
        if (selected.some((item) => String(item.id) === String(product.id))) {
          setMessage('That product is already selected.');
          return;
        }
        if (selected.length && product.group !== selected[0].group) {
          setMessage(`You are comparing ${selected[0].group}. Remove selected products to switch categories.`, 'warning');
          return;
        }
        if (selected.length >= maxCompare) {
          setMessage(`You can compare up to ${maxCompare} products at a time.`, 'warning');
          return;
        }
        preserveScroll(() => {
          selected.push(product);
          activeGroup = product.group;
          pickerOpen = true;
          input.value = '';
          mobileSpecsExpanded = false;
          renderAll();
          updateShareUrl();
        });
      }

      function removeProduct(id) {
        preserveScroll(() => {
          selected = selected.filter((product) => String(product.id) !== String(id));
          if (!selected.length) {
            pickerOpen = true;
            mobileSpecsExpanded = false;
          }
          renderAll();
          updateShareUrl();
        });
      }

      function clearComparison() {
        preserveScroll(() => {
          selected = [];
          pickerOpen = true;
          mobileSpecsExpanded = false;
          input.value = '';
          activeGroup = 'all';
          renderAll();
          updateShareUrl();
          setMessage('Comparison cleared.');
          setStatus('Ready', 'Choose a category or search below.');
        });
      }

      function renderSelected() {
        if (!selected.length) {
          selectedEl.innerHTML = '';
          if (trayTitle) trayTitle.textContent = 'No products selected';
          if (traySubtitle) traySubtitle.textContent = 'Add two or more matching products to compare price, specs, SKU, brand, and best fit.';
          if (pickerToggleButton) pickerToggleButton.hidden = true;
          return;
        }

        if (trayTitle) trayTitle.textContent = selected.length === 1 ? 'One product selected' : `${selected.length} products selected`;
        if (traySubtitle) traySubtitle.textContent = selected.length === 1 ? `Comparison locked to ${selected[0].group}. Add another product from the same group.` : `Comparing ${selected.length} products in ${selected[0].group}.`;
        if (pickerToggleButton) {
          pickerToggleButton.hidden = selected.length < 2 || selected.length >= maxCompare;
          pickerToggleButton.textContent = pickerOpen ? 'Hide product picker' : 'Add more products';
        }
        selectedEl.innerHTML = selected.map((product) => `
          <article class="ggcp-selected-card">
            <div class="ggcp-selected-card__media">${imageHTML(product)}</div>
            <div class="ggcp-selected-card__body"><div class="ggcp-selected-title-pill"><span>${titleHTML(product)}</span></div></div>
            <button class="ggcp-remove" type="button" data-remove="${escapeHTML(product.id)}" aria-label="Remove ${escapeHTML(product.title || 'product')}"></button>
          </article>
        `).join('');
      }

      function renderPickerState() {
        const panel = root.querySelector('[data-gg-picker-panel]');
        if (!panel) return;
        panel.hidden = selected.length >= 2 && !pickerOpen;
      }

      function unitToWatts(value, unit) {
        const lower = String(unit || '').toLowerCase();
        return lower.includes('kw') || lower.includes('kilowatt') ? value * 1000 : value;
      }

      function cleanMetricText(value) {
        return stripHTML(formatSpecValue(value)).replace(/,/g, '').trim();
      }

      function extractAllWatts(rawValue, options = {}) {
        const text = cleanMetricText(rawValue);
        const values = [];
        const seen = new Set();
        const ignorePaired = options.ignorePaired !== false;

        const segments = text.split(/;|\n|\r|<br\s*\/?\s*>/i).map((item) => item.trim()).filter(Boolean);
        const parts = segments.length ? segments : [text];

        parts.forEach((part) => {
          const lower = part.toLowerCase();
          if (ignorePaired && /(2\s*\*|two\s+units?|parallel|with\s+240v\s+box|\+\s*240v\s+box)/i.test(lower)) return;

          const multiplierRegex = /(\d+(?:\.\d+)?)\s*(?:x|\*)\s*(\d+(?:\.\d+)?)\s*(kilowatts?|kw|watts?|w)\b/gi;
          let multiplierMatch;
          while ((multiplierMatch = multiplierRegex.exec(part)) !== null) {
            const count = Number(multiplierMatch[1]);
            const value = Number(multiplierMatch[2]);
            const watts = Math.round(count * unitToWatts(value, multiplierMatch[3]));
            if (Number.isFinite(watts) && watts > 0 && !seen.has(watts)) {
              seen.add(watts);
              values.push(watts);
            }
          }

          const directRegex = /(\d+(?:\.\d+)?)\s*(kilowatts?|kw|watts?|w)\b/gi;
          let directMatch;
          while ((directMatch = directRegex.exec(part)) !== null) {
            const watts = Math.round(unitToWatts(Number(directMatch[1]), directMatch[2]));
            if (Number.isFinite(watts) && watts > 0 && !seen.has(watts)) {
              seen.add(watts);
              values.push(watts);
            }
          }
        });

        return values.sort((a, b) => b - a);
      }

      function extractWatts(rawValue, options) {
        const all = extractAllWatts(rawValue, options);
        return all.length ? all[0] : null;
      }

      function extractWattHours(rawValue) {
        const text = cleanMetricText(rawValue);
        const match = text.match(/(\d+(?:\.\d+)?)\s*(kwh|kilowatt hours?|kilowatt-hours?|wh|watt hours?|watt-hours?)\b/i);
        if (!match) return null;
        const value = Number(match[1]);
        if (!Number.isFinite(value)) return null;
        const unit = match[2].toLowerCase();
        return unit.includes('kwh') || unit.includes('kilowatt') ? value * 1000 : value;
      }

      function extractCycles(rawValue) {
        const text = cleanMetricText(rawValue);
        const match = text.match(/(\d+(?:\.\d+)?)\s*\+?\s*cycles?/i);
        if (!match) return null;
        const value = Number(match[1]);
        return Number.isFinite(value) ? value : null;
      }

      function extractWeightPounds(rawValue) {
        const text = cleanMetricText(rawValue);
        const lbMatch = text.match(/(\d+(?:\.\d+)?)\s*(lb|lbs|pounds?)\b/i);
        if (lbMatch) return Number(lbMatch[1]);
        const kgMatch = text.match(/(\d+(?:\.\d+)?)\s*(kg|kilograms?)\b/i);
        if (kgMatch) return Number(kgMatch[1]) * 2.20462;
        return null;
      }

      function extractHours(rawValue) {
        const text = cleanMetricText(rawValue);
        const match = text.match(/(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours)\b/i);
        if (!match) return null;
        const value = Number(match[1]);
        return Number.isFinite(value) ? value : null;
      }

      function extractNumber(rawValue) {
        const text = cleanMetricText(rawValue);
        const match = text.match(/(\d+(?:\.\d+)?)/);
        if (!match) return null;
        const value = Number(match[1]);
        return Number.isFinite(value) ? value : null;
      }

      function formatWatts(value) {
        if (!Number.isFinite(value)) return 'Not listed';
        if (value >= 1000) {
          const kw = value / 1000;
          return `${Number.isInteger(kw) ? kw.toFixed(0) : kw.toFixed(1)}kW`;
        }
        return `${Math.round(value)}W`;
      }

      function formatWh(value) {
        if (!Number.isFinite(value)) return 'Not listed';
        return `${Math.round(value).toLocaleString()}Wh`;
      }

      function formatHours(hours) {
        if (!Number.isFinite(hours) || hours <= 0) return 'Not listed';
        if (hours < 1) return `${Math.round(hours * 60)} min`;
        if (hours < 10) return `${hours.toFixed(1).replace(/\.0$/, '')} hours`;
        return `${Math.round(hours)} hours`;
      }

      function getFirstSpecValue(product, canonicals) {
        const entries = Object.entries(product.specs || {});
        for (const [key, value] of entries) {
          const canonical = canonicalSpecKey(key);
          if (canonicals.includes(canonical)) return value;
        }
        return null;
      }

      function getProductPriceDollars(product) {
        const raw = Number(product.priceRaw);
        if (Number.isFinite(raw) && raw > 0) return raw / 100;
        const parsed = Number(String(product.price || '').replace(/[^0-9.]/g, ''));
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      }

      function getProductCapacityWh(product) {
        return extractWattHours(getFirstSpecValue(product, ['capacity']));
      }

      function getProductAcOutputWatts(product) {
        const values = [
          getFirstSpecValue(product, ['ac output']),
          getFirstSpecValue(product, ['120v ac output']),
          getFirstSpecValue(product, ['240v ac output'])
        ].flatMap((value) => value ? extractAllWatts(value, { ignorePaired: true }) : []);
        return values.length ? Math.max(...values) : null;
      }

      function getProductSurgeWatts(product) {
        const text = cleanMetricText([
          getFirstSpecValue(product, ['ac output']),
          getFirstSpecValue(product, ['120v ac output']),
          getFirstSpecValue(product, ['240v ac output'])
        ].filter(Boolean).join(' '));
        const match = text.match(/(?:surge|peak|x\s*-?boost)[^0-9]*(\d+(?:\.\d+)?)\s*(kw|kilowatts?|w|watts?)/i);
        return match ? unitToWatts(Number(match[1]), match[2]) : null;
      }

      function getProductSolarInputWatts(product) {
        return extractWatts(getFirstSpecValue(product, ['solar input']), { ignorePaired: false });
      }

      function getProductAcInputWatts(product) {
        return extractWatts(getFirstSpecValue(product, ['ac input']), { ignorePaired: false });
      }

      function getProductAcInputWattOptions(product) {
        const raw = getFirstSpecValue(product, ['ac input']);
        const options = raw ? extractAllWatts(raw, { ignorePaired: false }) : [];
        const maxInput = getProductAcInputWatts(product);
        if (Number.isFinite(maxInput) && maxInput > 0 && !options.includes(Math.round(maxInput))) options.unshift(Math.round(maxInput));
        return Array.from(new Set(options)).filter((value) => Number.isFinite(value) && value > 0).sort((a, b) => b - a);
      }

      function getProductCycleLife(product) {
        return extractCycles(getFirstSpecValue(product, ['cycle life']));
      }

      function getProductChargeHours(product) {
        return extractHours(getFirstSpecValue(product, ['charge time']));
      }

      function getProductExpandedCapacityWh(product) {
        return extractWattHours(getFirstSpecValue(product, ['extra battery']));
      }

      function getProductWeight(product) {
        return extractWeightPounds(getFirstSpecValue(product, ['weight']));
      }

      function productSupports240v(product) {
        const text = Object.entries(product.specs || {}).map(([key, value]) => `${key} ${formatSpecValue(value)}`).join(' ').toLowerCase();
        return /\b240\s*v\b/.test(text) ? 100 : 0;
      }

      function productSupportsRv(product) {
        const text = Object.entries(product.specs || {}).map(([key, value]) => `${key} ${formatSpecValue(value)}`).join(' ').toLowerCase();
        return /tt\s*30|tt30|rv outlet|rv/.test(text) ? 100 : 0;
      }

      function productUpsScore(product) {
        const text = Object.entries(product.specs || {}).map(([key, value]) => `${key} ${formatSpecValue(value)}`).join(' ').toLowerCase();
        if (/ups[^0-9]{0,12}0\s*ms|0\s*ms[^a-z0-9]{0,12}ups|0ms/.test(text)) return 100;
        if (/ups/.test(text)) return 75;
        return 0;
      }

      function productPortScore(product) {
        const text = Object.entries(product.specs || {}).map(([key, value]) => `${key} ${formatSpecValue(value)}`).join(' ').toLowerCase();
        let score = 0;
        if (/usb\s*c|usb-c/.test(text)) score += 35;
        if (/usb\s*a|usb-a/.test(text)) score += 20;
        if (/\b12v\b|cigar|cigarette/.test(text)) score += 15;
        if (/tt\s*30|tt30|nema|anderson|xt60|5521|5525/.test(text)) score += 20;
        if (/wi-fi|wifi|bluetooth|app/.test(text)) score += 10;
        return Math.min(100, score);
      }

      function productQuietScore(product) {
        const text = Object.entries(product.specs || {}).map(([key, value]) => `${key} ${formatSpecValue(value)}`).join(' ').toLowerCase();
        if (/quiet|silent|low noise|fanless/.test(text)) return 100;
        return 65;
      }

      function portabilityScoreFromWeight(weight) {
        if (!Number.isFinite(weight)) return 55;
        if (weight <= 25) return 100;
        if (weight <= 45) return 82;
        if (weight <= 75) return 62;
        if (weight <= 110) return 42;
        return 25;
      }

      function clampScore(score) {
        if (!Number.isFinite(score)) return 0;
        return Math.max(0, Math.min(100, score));
      }

      function calculateRawMetrics(product) {
        const price = getProductPriceDollars(product);
        const capacity = getProductCapacityWh(product);
        const acOutput = getProductAcOutputWatts(product);
        const surgeOutput = getProductSurgeWatts(product);
        const solarInput = getProductSolarInputWatts(product);
        const acInput = getProductAcInputWatts(product);
        const cycleLife = getProductCycleLife(product);
        const weight = getProductWeight(product);
        const expandedCapacity = getProductExpandedCapacityWh(product);
        const listedChargeHours = getProductChargeHours(product);
        const whPerDollar = Number.isFinite(price) && price > 0 && Number.isFinite(capacity) && capacity > 0 ? capacity / price : null;
        const lifetimeValue = Number.isFinite(price) && price > 0 && Number.isFinite(capacity) && Number.isFinite(cycleLife) ? capacity * cycleLife / price : null;
        const acRecharge = Number.isFinite(capacity) && capacity > 0 && Number.isFinite(acInput) && acInput > 0 ? (capacity / acInput) * 1.12 : listedChargeHours;
        const expansionRatio = Number.isFinite(capacity) && capacity > 0 && Number.isFinite(expandedCapacity) && expandedCapacity > 0 ? expandedCapacity / capacity : null;
        return {
          capacity,
          capacityValue: whPerDollar,
          priceValue: whPerDollar,
          lifetimeValue,
          acOutput,
          surgeOutput,
          solarInput,
          acRecharge,
          expansion: expansionRatio,
          cycleLife,
          support240v: productSupports240v(product),
          rvSupport: productSupportsRv(product),
          ups: productUpsScore(product),
          portability: portabilityScoreFromWeight(weight),
          ports: productPortScore(product),
          quietFit: productQuietScore(product)
        };
      }

      function metricLabel(metric) {
        const labels = {
          capacity: 'Backup capacity',
          capacityValue: 'Capacity value',
          priceValue: 'Price value',
          lifetimeValue: 'Lifetime value',
          acOutput: 'AC output',
          surgeOutput: 'Surge output',
          solarInput: 'Solar input',
          acRecharge: 'AC recharge',
          expansion: 'Expansion',
          cycleLife: 'Cycle life',
          support240v: '240V support',
          rvSupport: 'RV support',
          ups: 'UPS support',
          portability: 'Portability',
          ports: 'Ports',
          quietFit: 'Quiet fit'
        };
        return labels[metric] || metric;
      }

      function metricDirection(metric) {
        return metric === 'acRecharge' ? 'lower' : 'higher';
      }

      function metricScore(product, metric, comparisonProducts) {
        const rawValues = comparisonProducts.map((item) => {
          const metrics = calculateRawMetrics(item);
          const value = metrics[metric];
          return { product: item, value: Number.isFinite(value) ? value : null };
        });
        const current = rawValues.find((entry) => entry.product.id === product.id);
        if (!current || !Number.isFinite(current.value)) return 55;
        const validValues = rawValues.map((entry) => entry.value).filter((value) => Number.isFinite(value) && value > 0);
        if (!validValues.length) return 55;
        if (metricDirection(metric) === 'lower') {
          const min = Math.min(...validValues);
          if (!Number.isFinite(min) || min <= 0 || current.value <= 0) return 55;
          return clampScore((min / current.value) * 100);
        }
        const max = Math.max(...validValues);
        if (!Number.isFinite(max) || max <= 0) return 55;
        return clampScore((current.value / max) * 100);
      }

      function getUseCaseConfig(key) {
        return USE_CASES[key] || USE_CASES.homeBackup;
      }

      function calculateUseCaseValue(product, useCaseKey, comparisonProducts = selected) {
        const config = getUseCaseConfig(useCaseKey);
        const entries = Object.entries(config.weights || {});
        let totalWeight = 0;
        let totalScore = 0;
        const metricScores = [];
        entries.forEach(([metric, weight]) => {
          const score = metricScore(product, metric, comparisonProducts);
          const safeWeight = Number(weight);
          if (!Number.isFinite(score) || !Number.isFinite(safeWeight) || safeWeight <= 0) return;
          totalWeight += safeWeight;
          totalScore += score * safeWeight;
          metricScores.push({ metric, label: metricLabel(metric), score });
        });
        return {
          score: totalWeight > 0 ? Math.round(totalScore / totalWeight) : 0,
          metrics: metricScores.sort((a, b) => b.score - a.score)
        };
      }

      function getFitLabel(score) {
        if (!Number.isFinite(score)) return 'Fit not available';
        if (score >= 90) return 'Excellent fit';
        if (score >= 80) return 'Strong fit';
        if (score >= 70) return 'Good fit';
        if (score >= 60) return 'Practical fit';
        return 'Niche fit';
      }

      function getBestUseCase(product) {
        const pool = selected.length ? selected : products.slice(0, 4);
        return Object.keys(USE_CASES)
          .map((key) => ({ key, label: USE_CASES[key].label, score: calculateUseCaseValue(product, key, pool).score }))
          .sort((a, b) => b.score - a.score)[0] || null;
      }

      function renderUseCasePanel() {
        if (!useCasePanelEl) return;
        useCasePanelEl.hidden = !selected.length;
        if (!selected.length) {
          if (valueCardsEl) valueCardsEl.innerHTML = '';
          return;
        }
        const config = getUseCaseConfig(activeUseCase);
        if (useCaseTitleEl) useCaseTitleEl.textContent = `${config.label} Value`;
        if (useCaseDescriptionEl) useCaseDescriptionEl.textContent = config.description;
        if (useCaseTabsEl) {
          useCaseTabsEl.innerHTML = Object.entries(USE_CASES).map(([key, item]) => `
            <button class="ggcp-usecase-tab ${key === activeUseCase ? 'is-active' : ''}" type="button" data-usecase="${escapeHTML(key)}">${escapeHTML(item.label)}</button>
          `).join('');
        }
        renderValueCards();
      }

      function renderValueCards() {
        if (!valueCardsEl) return;
        const count = Math.max(1, Math.min(5, selected.length || 1));
        valueCardsEl.style.setProperty('--ggcp-value-card-count', String(count));
        valueCardsEl.innerHTML = selected.map((product) => {
          const result = calculateUseCaseValue(product, activeUseCase, selected);
          const score = Number.isFinite(result.score) ? result.score : 0;
          const topMetrics = result.metrics.slice(0, 4);
          const best = getBestUseCase(product);
          return `
            <article class="ggcp-value-card">
              <div class="ggcp-value-card__top">
                <span class="ggcp-value-ring" style="--ggcp-ring-score:${score}"><strong>${score}</strong></span>
                <div>
                  <h3>${titleHTML(product)}</h3>
                  <span class="ggcp-value-card__label">${escapeHTML(getFitLabel(score))}</span>
                </div>
              </div>
              <p class="ggcp-value-card__note"><strong>Best fit:</strong><br>${best ? escapeHTML(best.label) : 'Not enough data'}<br>${escapeHTML(getUseCaseConfig(activeUseCase).label)} score is based on use case, not a universal good or bad rating.</p>
              <div class="ggcp-value-metrics">
                ${topMetrics.map((entry) => `
                  <div class="ggcp-value-metric">
                    <span>${escapeHTML(entry.label)}</span>
                    <span class="ggcp-value-metric__bar" aria-hidden="true"><span style="--ggcp-metric-score:${Math.round(entry.score)}%"></span></span>
                    <strong>${Math.round(entry.score)}</strong>
                  </div>
                `).join('')}
              </div>
            </article>
          `;
        }).join('');
      }

      function getComparableSpecRows() {
        const rowsByKey = new Map();
        selected.forEach((product, productIndex) => {
          Object.entries(product.specs || {}).forEach(([label, rawValue]) => {
            if (isBlankSpecValue(rawValue)) return;
            const canonical = canonicalSpecKey(label);
            if (!canonical) return;
            if (!rowsByKey.has(canonical)) {
              rowsByKey.set(canonical, { canonical, labels: [], firstSeen: productIndex, valuesByProductId: new Map() });
            }
            const row = rowsByKey.get(canonical);
            row.labels.push(label);
            row.valuesByProductId.set(String(product.id), rawValue);
          });
        });
        const rows = Array.from(rowsByKey.values()).map((row) => ({ ...row, label: chooseSpecLabel(row.labels, row.canonical) }));
        rows.sort((a, b) => {
          const ai = PRIORITY_ROWS.indexOf(a.canonical);
          const bi = PRIORITY_ROWS.indexOf(b.canonical);
          const aSafe = ai === -1 ? 999 : ai;
          const bSafe = bi === -1 ? 999 : bi;
          if (aSafe !== bSafe) return aSafe - bSafe;
          return a.label.localeCompare(b.label);
        });
        return rows;
      }

      function formatAcInputOptionLabel(watts) {
        if (!Number.isFinite(watts)) return 'AC estimate';
        if (watts >= 3400 && watts <= 3800) return '30A AC cable';
        if (watts >= 1650 && watts <= 1900) return '15A AC cable';
        return `${formatWatts(watts)} AC input`;
      }

      function renderCapacityChargeDetailsHTML(product) {
        const rows = [];
        const capacity = getProductCapacityWh(product);
        const acOptions = getProductAcInputWattOptions(product);
        const listedHours = getProductChargeHours(product);
        if (Number.isFinite(capacity) && capacity > 0 && acOptions.length) {
          acOptions.forEach((watts) => {
            const hours = (capacity / watts) * 1.12;
            if (!Number.isFinite(hours)) return;
            rows.push({ label: formatAcInputOptionLabel(watts), value: formatHours(hours), note: `Estimated from ${formatWatts(watts)} AC input and ${Math.round(capacity).toLocaleString()}Wh capacity.` });
          });
        }
        if (!rows.length && Number.isFinite(listedHours)) rows.push({ label: 'Listed charge time', value: formatHours(listedHours), note: 'Manufacturer listed charge time when provided.' });
        if (!rows.length) return '';
        return renderInlineDetails('View charge times', rows);
      }

      function renderSolarChargeDetailsHTML(product) {
        const solarInput = getProductSolarInputWatts(product);
        const capacity = getProductCapacityWh(product);
        if (!Number.isFinite(solarInput) || solarInput <= 0 || !Number.isFinite(capacity) || capacity <= 0) return '';
        const baseSteps = [400, 800, 1200, 2000];
        const maxStep = Math.round(solarInput);
        const steps = Array.from(new Set([...baseSteps, maxStep])).filter((value) => Number.isFinite(value) && value > 0).sort((a, b) => a - b);
        const rows = steps.map((watts) => {
          const usableWatts = Math.min(watts, solarInput);
          const hours = (capacity / usableWatts) * 1.15;
          if (!Number.isFinite(hours)) return null;
          const capped = watts > solarInput;
          return { label: capped ? `${formatWatts(watts)} requested` : `${formatWatts(watts)} solar`, value: formatHours(hours), note: capped ? `Limited by ${formatWatts(solarInput)} max solar input.` : `Estimated from ${formatWatts(usableWatts)} solar and ${Math.round(capacity).toLocaleString()}Wh capacity.` };
        }).filter(Boolean);
        if (!rows.length) return '';
        return renderInlineDetails('View solar charge times', rows);
      }

      function renderCycleLifeDetailsHTML(product) {
        const cycles = getProductCycleLife(product);
        const capacity = getProductCapacityWh(product);
        if (!Number.isFinite(cycles) || cycles <= 0 || !Number.isFinite(capacity) || capacity <= 0) return '';
        const lifetimeWh = capacity * cycles;
        const remaining80 = capacity * 0.8;
        const rows = [
          { label: 'Daily full cycle use', value: `${(cycles / 365).toFixed(1).replace(/\.0$/, '')} years`, note: `Based on ${Math.round(cycles).toLocaleString()} rated cycles.` },
          { label: 'Rated lifetime energy', value: formatWh(lifetimeWh), note: `Capacity multiplied by rated cycles before reaching the listed end of life threshold.` },
          { label: 'Capacity at 80%', value: formatWh(remaining80), note: `Estimated usable capacity once the battery reaches 80% of original capacity.` }
        ];
        return renderInlineDetails('View lifespan estimate', rows, 'ggcp-inline-estimate--cycle');
      }

      function renderInlineDetails(summary, rows, modifierClass = '') {
        const classes = ['ggcp-inline-estimate', modifierClass].filter(Boolean).join(' ');
        return `
          <details class="${escapeHTML(classes)}">
            <summary>${escapeHTML(summary)}</summary>
            <div class="ggcp-inline-estimate__body">
              ${rows.map((row) => `
                <div class="ggcp-inline-estimate__row">
                  <span>${escapeHTML(row.label)}</span>
                  <div><strong>${escapeHTML(row.value)}</strong>${row.note ? `<span class="ggcp-fit-note">${escapeHTML(row.note)}</span>` : ''}</div>
                </div>
              `).join('')}
            </div>
          </details>
        `;
      }

      function parseDimensionInches(value) {
        const text = cleanMetricText(value);
        if (!text) return null;

        const rawNumbers = (text.match(/\d+(?:\.\d+)?/g) || []).slice(0, 3).map(Number).filter((num) => Number.isFinite(num));
        if (rawNumbers.length < 3) return null;

        const mentionsMm = /\bmm\b|millimeters?|millimetres?/i.test(text);
        const mentionsInches = /\binches?\b|\bin\.?\b|\"/i.test(text);
        let sourceUnit = 'in';

        if (mentionsMm && !mentionsInches) sourceUnit = 'mm';
        if (mentionsMm && rawNumbers.every((num) => num > 100)) sourceUnit = 'mm';
        if (mentionsInches) sourceUnit = 'in';

        const dimensions = rawNumbers.map((num) => sourceUnit === 'mm' ? num / 25.4 : num);
        if (dimensions.some((num) => !Number.isFinite(num) || num <= 0)) return null;

        return {
          length: dimensions[0],
          width: dimensions[1],
          height: dimensions[2],
          values: dimensions,
          rawValues: rawNumbers,
          sourceUnit
        };
      }

      function renderDimensionConversionHTML(value) {
        const dimensions = parseDimensionInches(value);
        if (!dimensions) return '';
        const pretty = dimensions.values.map(formatFeetInches).join(' × ');
        return `<span class="ggcp-fit-note">Approx. ${pretty}.</span>`;
      }

      function formatDimensionInches(value) {
        if (!Number.isFinite(value)) return '0 in';
        const rounded = Number(value.toFixed(1));
        return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} in`;
      }

      function polygonPoints(points) {
        return points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');
      }

      function renderSizeComparisonHTML(productsToCompare) {
        const dimensionItems = productsToCompare.map((product, index) => {
          const dimensions = parseDimensionInches(getFirstSpecValue(product, ['dimensions']));
          if (!dimensions) return null;
          return {
            product,
            index,
            length: dimensions.length,
            width: dimensions.width,
            height: dimensions.height,
            area: dimensions.length * dimensions.width
          };
        }).filter(Boolean);

        if (dimensionItems.length < 2) return '<span class="ggcp-fit-note">Not enough dimension data to compare size.</span>';

        const sorted = [...dimensionItems].sort((a, b) => b.area - a.area);
        const maxLength = Math.max(...sorted.map((item) => item.length));
        const maxWidth = Math.max(...sorted.map((item) => item.width));
        const maxHeight = Math.max(...sorted.map((item) => item.height));
        const scale = Math.min(16, 520 / Math.max(maxLength, 1), 260 / Math.max(maxWidth, 1));
        const originX = 140;
        const originY = 285;
        const colors = [
          { top: '#FBB500', front: '#B97800', side: '#EE8E00', text: '#F5F5F5', line: '#F5F5F5' },
          { top: '#EE8E00', front: '#9C5900', side: '#C97500', text: '#F5F5F5', line: '#F5F5F5' },
          { top: '#284055', front: '#031321', side: '#001B30', text: '#F5F5F5', line: '#F5F5F5' },
          { top: '#687686', front: '#284055', side: '#26385B', text: '#F5F5F5', line: '#F5F5F5' }
        ];

        const layers = sorted.map((item, sortedIndex) => {
          const length = item.length * scale;
          const width = item.width * scale;
          const u = { x: length, y: -length * 0.34 };
          const v = { x: width * 0.72, y: width * 0.34 };
          const thickness = Math.max(8, Math.min(28, item.height / Math.max(maxHeight, 1) * 22));
          const insetX = sortedIndex * 24;
          const insetY = -sortedIndex * 22;
          const base = { x: originX + insetX, y: originY + insetY };
          const p0 = base;
          const p1 = { x: base.x + u.x, y: base.y + u.y };
          const p2 = { x: p1.x + v.x, y: p1.y + v.y };
          const p3 = { x: base.x + v.x, y: base.y + v.y };
          const p0b = { x: p0.x, y: p0.y + thickness };
          const p1b = { x: p1.x, y: p1.y + thickness };
          const p2b = { x: p2.x, y: p2.y + thickness };
          const p3b = { x: p3.x, y: p3.y + thickness };
          const color = colors[sortedIndex % colors.length];
          const title = escapeHTML(item.product.shortTitle || item.product.title || `Product ${item.index + 1}`);
          const dimensions = `${formatDimensionInches(item.length)} × ${formatDimensionInches(item.width)} × ${formatDimensionInches(item.height)}`;
          const lengthLabel = formatDimensionInches(item.length);
          const widthLabel = formatDimensionInches(item.width);
          const heightLabel = formatDimensionInches(item.height);
          const isBaseLayer = sortedIndex === 0;
          const lengthPoint = isBaseLayer
            ? { x: (p3b.x + p2b.x) / 2, y: (p3b.y + p2b.y) / 2 + 24, rotate: -19 }
            : { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 + 10, rotate: -19 };
          const widthPoint = isBaseLayer
            ? { x: (p0b.x + p3b.x) / 2 - 28, y: (p0b.y + p3b.y) / 2 + 8, rotate: 18, anchor: 'end' }
            : { x: (p0.x + p3.x) / 2 + 5, y: (p0.y + p3.y) / 2 + 2, rotate: 18, anchor: 'middle' };
          const heightPoint = isBaseLayer
            ? { x: p2.x + 16, y: (p2.y + p2b.y) / 2 + 5, anchor: 'start' }
            : { x: p0.x - 12, y: (p0.y + p0b.y) / 2 + 5, anchor: 'end' };
          return `
            <g class="ggcp-size-layer ggcp-size-layer--${sortedIndex + 1}">
              <polygon points="${polygonPoints([p0, p1, p2, p3])}" fill="${color.top}" opacity="${isBaseLayer ? '0.92' : '0.98'}"></polygon>
              <polygon points="${polygonPoints([p0, p3, p3b, p0b])}" fill="${color.side}" opacity="0.98"></polygon>
              <polygon points="${polygonPoints([p3, p2, p2b, p3b])}" fill="${color.front}" opacity="0.96"></polygon>
              <text class="ggcp-size-label ggcp-size-label--length" x="${lengthPoint.x}" y="${lengthPoint.y}" text-anchor="middle" transform="rotate(${lengthPoint.rotate} ${lengthPoint.x} ${lengthPoint.y})">${lengthLabel}</text>
              <text class="ggcp-size-label ggcp-size-label--width" x="${widthPoint.x}" y="${widthPoint.y}" text-anchor="${widthPoint.anchor}" transform="rotate(${widthPoint.rotate} ${widthPoint.x} ${widthPoint.y})">${widthLabel}</text>
              <text class="ggcp-size-label ggcp-size-label--height" x="${heightPoint.x}" y="${heightPoint.y}" text-anchor="${heightPoint.anchor}">${heightLabel}</text>
              <title>${title}: ${dimensions}</title>
            </g>
          `;
        }).join('');

        const legend = sorted.map((item, sortedIndex) => {
          const color = colors[sortedIndex % colors.length].top;
          const title = escapeHTML(item.product.shortTitle || item.product.title || `Product ${item.index + 1}`);
          const dimensions = `${formatDimensionInches(item.length)} × ${formatDimensionInches(item.width)} × ${formatDimensionInches(item.height)}`;
          return `<li><span style="--ggcp-size-color:${color}"></span><strong>${title}</strong><em>${dimensions}</em></li>`;
        }).join('');

        const largest = sorted[0];
        const smallest = sorted[sorted.length - 1];
        const areaDiff = smallest && largest && largest.area > 0 ? Math.round((1 - smallest.area / largest.area) * 100) : 0;
        const note = areaDiff > 0 ? `${escapeHTML(smallest.product.shortTitle || smallest.product.title)} has about ${areaDiff}% less footprint area than ${escapeHTML(largest.product.shortTitle || largest.product.title)}.` : 'Footprint area is very similar between these products.';

        return `
          <details class="ggcp-size-compare">
            <summary>Compare size</summary>
            <div class="ggcp-size-compare__body">
              <div class="ggcp-size-compare__chart" role="img" aria-label="Layered size comparison chart">
                <svg viewBox="0 0 900 390" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                  <text class="ggcp-size-title" x="24" y="36">Size comparison</text>
                  ${layers}
                </svg>
              </div>
              <ul class="ggcp-size-legend">${legend}</ul>
              <p class="ggcp-size-note">${note}</p>
            </div>
          </details>
        `;
      }

      function formatFeetInches(totalInches) {
        if (!Number.isFinite(totalInches)) return '';
        const feet = Math.floor(totalInches / 12);
        const inches = totalInches - feet * 12;
        const roundedInches = Number(inches.toFixed(1));
        const inchText = Number.isInteger(roundedInches) ? roundedInches.toFixed(0) : roundedInches.toFixed(1);
        if (feet <= 0) return `${inchText} in`;
        if (roundedInches <= 0) return `${feet} ft`;
        return `${feet} ft ${inchText} in`;
      }

      function renderSolarExplanationHTML(value, product) {
        const text = cleanMetricText(value);
        const solarInput = getProductSolarInputWatts(product);
        if (!text || !Number.isFinite(solarInput)) return '';

        const productName = getSolarDisplayName(product);
        const ports = parseSolarInputPorts(text);
        const portLines = ports.length ? ports.map((port) => renderSolarPortLine(port)).join('') : '';
        const summary = buildSolarSummary(productName, solarInput, ports);
        const voltageNote = buildSolarVoltageEducation(ports);

        return `
          <div class="ggcp-explain-note ggcp-solar-explain">
            <div class="ggcp-solar-lead"><strong>${escapeHTML(productName)} solar input:</strong> ${escapeHTML(summary)}</div>
            ${portLines ? `<div class="ggcp-solar-port-list">${portLines}</div>` : ''}
            ${voltageNote ? `<div class="ggcp-solar-helper">${escapeHTML(voltageNote)}</div>` : ''}
          </div>
        `;
      }

      function getSolarDisplayName(product) {
        const title = String((product && (product.shortTitle || product.title)) || 'This unit').replace(/\s+/g, ' ').trim();
        const modelMatch = title.match(/\b([A-Z]{0,8}\d{3,5}[A-Z0-9-]*(?:\s*LFP)?)\b/i);
        if (modelMatch) return modelMatch[1].replace(/\s+/g, ' ').trim();
        return title || 'This unit';
      }

      function parseSolarInputPorts(text) {
        const ports = [];
        const matches = Array.from(String(text || '').matchAll(/([A-Za-z0-9-]+)\s*:\s*([^;]+)/g));
        matches.forEach((match) => {
          const connector = String(match[1] || '').trim();
          const detail = String(match[2] || '').trim();
          if (!connector || !detail) return;
          const multiplierMatch = detail.match(/(\d+(?:\.\d+)?)\s*\*\s*(\d+(?:\.\d+)?)\s*w/i);
          const wattMatches = Array.from(detail.matchAll(/(\d+(?:\.\d+)?)\s*w\s*(?:max)?/gi)).map((m) => Number(m[1])).filter(Number.isFinite);
          const voltageMatch = detail.match(/voc\s*(\d+(?:\.\d+)?)\s*v?\s*~\s*(\d+(?:\.\d+)?)\s*v/i) || detail.match(/(\d+(?:\.\d+)?)\s*v\s*~\s*(\d+(?:\.\d+)?)\s*v/i);
          const ampMatch = detail.match(/(\d+(?:\.\d+)?)\s*a\s*max/i);
          const count = multiplierMatch ? Number(multiplierMatch[1]) : 1;
          const perInputWatts = multiplierMatch ? Number(multiplierMatch[2]) : (wattMatches.length ? Math.max(...wattMatches) : null);
          const totalWatts = multiplierMatch ? count * perInputWatts : perInputWatts;
          const minVoc = voltageMatch ? Number(voltageMatch[1]) : null;
          const maxVoc = voltageMatch ? Number(voltageMatch[2]) : null;
          const amps = ampMatch ? Number(ampMatch[1]) : null;
          ports.push({
            connector,
            detail,
            count: Number.isFinite(count) && count > 0 ? count : 1,
            perInputWatts: Number.isFinite(perInputWatts) ? perInputWatts : null,
            totalWatts: Number.isFinite(totalWatts) ? totalWatts : null,
            minVoc: Number.isFinite(minVoc) ? minVoc : null,
            maxVoc: Number.isFinite(maxVoc) ? maxVoc : null,
            amps: Number.isFinite(amps) ? amps : null
          });
        });
        return ports;
      }

      function describeConnector(connector) {
        const normalized = String(connector || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        if (normalized.includes('dc5521')) return 'DC5521 cylindrical input';
        if (normalized.includes('xt60')) return connector.replace(/xt60m/i, 'XT60-M').replace(/xt60/i, 'XT60');
        return connector;
      }

      function renderSolarPortLine(port) {
        const connector = describeConnector(port.connector);
        const parts = [];
        if (port.count > 1 && Number.isFinite(port.perInputWatts)) parts.push(`${port.count} inputs at ${formatWatts(port.perInputWatts)} each`);
        if (port.count <= 1 && Number.isFinite(port.perInputWatts)) parts.push(`${formatWatts(port.perInputWatts)} max`);
        if (port.count > 1 && Number.isFinite(port.totalWatts)) parts.push(`${formatWatts(port.totalWatts)} total`);
        if (Number.isFinite(port.minVoc) && Number.isFinite(port.maxVoc)) parts.push(`VOC ${formatNumber(port.minVoc)}V to ${formatNumber(port.maxVoc)}V`);
        if (Number.isFinite(port.amps)) parts.push(`${formatNumber(port.amps)}A max`);
        const detail = parts.length ? parts.join(' · ') : port.detail;
        return `<div class="ggcp-solar-port"><strong>${escapeHTML(connector)}:</strong> <span>${escapeHTML(detail)}</span></div>`;
      }

      function buildSolarSummary(productName, totalSolarInput, ports) {
        const totalText = `${formatWatts(totalSolarInput)} listed hardware input limit`;
        if (!ports.length) return `${totalText}. The connected solar array must stay within the listed voltage and current limits.`;
        const xt60Ports = ports.filter((port) => /xt60/i.test(port.connector));
        const barrelPorts = ports.filter((port) => /dc5521|barrel|cylindrical/i.test(port.connector));
        const pieces = [];
        if (ports.length === 1) {
          const port = ports[0];
          if (port.count > 1 && Number.isFinite(port.perInputWatts)) pieces.push(`offers ${port.count} ${describeConnector(port.connector)} solar inputs at ${formatWatts(port.perInputWatts)} each`);
          else pieces.push(`uses a ${describeConnector(port.connector)} solar input`);
        } else {
          pieces.push(`uses ${ports.length} listed solar input types`);
          if (barrelPorts.length) pieces.push(`${barrelPorts.map((port) => describeConnector(port.connector)).join(', ')} for lower wattage charging`);
          if (xt60Ports.length) pieces.push(`${xt60Ports.map((port) => describeConnector(port.connector)).join(', ')} for higher wattage charging`);
        }
        return `${productName} ${pieces.join(', ')}. Together, the solar side is limited to ${totalText}.`;
      }

      function buildSolarVoltageEducation(ports) {
        if (!ports.length) return '';
        const ranges = ports.filter((port) => Number.isFinite(port.minVoc) && Number.isFinite(port.maxVoc));
        if (!ranges.length) return '';
        const highestMinimum = Math.max(...ranges.map((port) => port.minVoc));
        const minimumText = ranges.map((port) => `${describeConnector(port.connector)} starts at ${formatNumber(port.minVoc)}V VOC`).join('; ');
        const seriesNote = highestMinimum >= 28
          ? 'Because one or more inputs require about 30V or more before the MPPT can operate, a single 12V panel is usually not enough. Most 12V panels sit around 18V to 25V VOC, so those higher voltage ports normally need at least two 12V panels in series while staying under the maximum VOC limit.'
          : 'Lower voltage inputs can usually start from a smaller panel string, but the panel string still has to remain under the maximum VOC limit.';
        return `${minimumText}. ${seriesNote}`;
      }

      function formatNumber(value) {
        if (!Number.isFinite(value)) return '';
        return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
      }

      function renderComparableSpecCellHTML(specRow, product) {
        const value = specRow.valuesByProductId.get(String(product.id));
        let html = `<div class="ggcp-spec-value">${formatSpecHTML(value)}</div>`;
        if (specRow.canonical === 'dimensions') html += renderDimensionConversionHTML(value);
        if (specRow.canonical === 'capacity') html += renderCapacityChargeDetailsHTML(product);
        if (specRow.canonical === 'cycle life') html += renderCycleLifeDetailsHTML(product);
        if (specRow.canonical === 'solar input') {
          html += renderSolarExplanationHTML(value, product);
          html += renderSolarChargeDetailsHTML(product);
        }
        return html;
      }

      function buildWeightInsights(specRow) {
        const values = selected.map((product) => ({ product, value: extractWeightPounds(specRow.valuesByProductId.get(String(product.id))) })).filter((entry) => Number.isFinite(entry.value));
        if (values.length < 2) return null;
        const sorted = [...values].sort((a, b) => a.value - b.value);
        const lightest = sorted[0].value;
        const secondLightest = sorted[1] ? sorted[1].value : lightest;
        const heaviest = sorted[sorted.length - 1].value;
        if (!Number.isFinite(lightest) || lightest === heaviest) return null;
        const savings = Math.max(0, secondLightest - lightest);
        const insights = new Map();
        values.forEach(({ product, value }) => {
          if (Math.abs(value - lightest) > 0.00001) return;
          insights.set(String(product.id), { isBest: true, tone: 'best', kind: 'weight', label: savings >= 1 ? `${Math.round(savings)} lb lighter` : 'Lighter only', width: 100 });
        });
        return { hint: '', byProductId: insights };
      }

      function buildPriceValueInsights() {
        const values = selected.map((product) => ({ product, value: getProductPriceDollars(product) })).filter((entry) => Number.isFinite(entry.value));
        if (values.length < 2) return null;
        const lowest = Math.min(...values.map((entry) => entry.value));
        const highest = Math.max(...values.map((entry) => entry.value));
        if (lowest === highest) return null;
        const insights = new Map();
        values.forEach(({ product, value }) => {
          if (Math.abs(value - lowest) > 0.00001) return;
          insights.set(String(product.id), { isBest: true, tone: 'best', label: 'Lower price', width: 100 });
        });
        return { hint: '', byProductId: insights };
      }

      function buildMetricInsights(canonical, valueGetter) {
        const metricMap = {
          capacity: { extractor: extractWattHours, label: 'More capacity', direction: 'higher' },
          'cycle life': { extractor: extractCycles, label: 'Higher cycle life', direction: 'higher' },
          'ac output': { extractor: (value) => extractWatts(value, { ignorePaired: true }), label: 'Higher output', direction: 'higher' },
          '120v ac output': { extractor: (value) => extractWatts(value, { ignorePaired: true }), label: 'Higher output', direction: 'higher' },
          '240v ac output': { extractor: (value) => extractWatts(value, { ignorePaired: true }), label: 'Higher output', direction: 'higher' },
          'solar input': { extractor: (value) => extractWatts(value, { ignorePaired: false }), label: 'Higher solar input', direction: 'higher' },
          'ac input': { extractor: (value) => extractWatts(value, { ignorePaired: false }), label: 'Faster AC input', direction: 'higher' },
          'charge time': { extractor: extractHours, label: 'Faster charge', direction: 'lower' }
        };
        const config = metricMap[canonical];
        if (!config) return null;
        const values = selected.map((product) => ({ product, value: config.extractor(valueGetter(product)) })).filter((entry) => Number.isFinite(entry.value));
        if (values.length < 2) return null;
        const best = config.direction === 'lower' ? Math.min(...values.map((entry) => entry.value)) : Math.max(...values.map((entry) => entry.value));
        const worst = config.direction === 'lower' ? Math.max(...values.map((entry) => entry.value)) : Math.min(...values.map((entry) => entry.value));
        if (!Number.isFinite(best) || !Number.isFinite(worst) || best === worst) return null;
        const insights = new Map();
        values.forEach(({ product, value }) => {
          const isBest = Math.abs(value - best) <= 0.00001;
          if (!isBest) return;
          insights.set(String(product.id), { isBest: true, tone: 'best', label: config.label, width: 100 });
        });
        return { hint: '', byProductId: insights };
      }

      function splitCellDetails(baseHtml) {
        const match = String(baseHtml).match(/([\s\S]*?)(<details[\s\S]*<\/details>\s*)$/i);
        if (!match) return { main: baseHtml, details: '' };
        return { main: match[1], details: match[2] };
      }

      function renderInsightCell(baseHtml, insight) {
        const parts = splitCellDetails(baseHtml);
        const hasDetails = Boolean(parts.details && parts.details.trim());
        const detailClass = hasDetails ? ' ggcp-value-stack--with-details' : '';
        if (!insight) {
          if (!hasDetails) return `${parts.main}${parts.details}`;
          return `
            <div class="ggcp-value-stack ggcp-value-stack--reserved${detailClass}">
              <div class="ggcp-spec-main">${parts.main}</div>
              <div class="ggcp-score-reserve ggcp-score-reserve--empty" aria-hidden="true"><span class="ggcp-score-slot ggcp-score-slot--empty"></span></div>
              ${parts.details}
            </div>
          `;
        }
        const width = Number.isFinite(insight.width) ? Math.max(0, Math.min(100, insight.width)) : 0;
        const toneClass = insight.tone === 'best' ? 'ggcp-delta-pill--best' : 'ggcp-delta-pill--down';
        const kindClass = insight.kind ? `ggcp-delta-pill--${escapeHTML(insight.kind)}` : '';
        if (insight.kind === 'weight') {
          return `
            <div class="ggcp-value-stack ggcp-value-stack--weight${detailClass}">
              <div class="ggcp-spec-main">${parts.main}</div>
              <div class="ggcp-score-reserve ggcp-score-reserve--weight">
                <span class="ggcp-delta-pill ${toneClass} ${kindClass}">${escapeHTML(insight.label)}</span>
              </div>
              ${parts.details}
            </div>
          `;
        }
        return `
          <div class="ggcp-value-stack ${insight.kind ? `ggcp-value-stack--${escapeHTML(insight.kind)}` : ''}${detailClass}">
            <div class="ggcp-spec-main">${parts.main}</div>
            <div class="ggcp-score-reserve">
              <span class="ggcp-score-bar" aria-hidden="true"><span style="--ggcp-score-width:${width}%"></span></span>
              <span class="ggcp-delta-pill ${toneClass} ${kindClass}">${escapeHTML(insight.label)}</span>
              ${insight.note ? `<span class="ggcp-insight-note">${escapeHTML(insight.note)}</span>` : ''}
            </div>
            ${parts.details}
          </div>
        `;
      }

      function rowLabelHTML(row) {
        if (!row.insights) return escapeHTML(row.label);
        return `<span class="ggcp-row-label__text">${escapeHTML(row.label)}</span>${row.insights.hint ? `<span class="ggcp-row-label__hint">${escapeHTML(row.insights.hint)}</span>` : ''}`;
      }

      function renderUseCaseIndexHTML(product) {
        const result = calculateUseCaseValue(product, activeUseCase, selected);
        const score = Number.isFinite(result.score) ? result.score : 0;
        return `
          <div class="ggcp-score-inline">
            <strong>${score}</strong>
            <span class="ggcp-score-bar" aria-hidden="true"><span style="--ggcp-score-width:${score}%"></span></span>
            <span class="ggcp-fit-note">${escapeHTML(getFitLabel(score))} for ${escapeHTML(getUseCaseConfig(activeUseCase).label)}.</span>
          </div>
        `;
      }

      function renderBestUseCaseHTML(product) {
        const best = getBestUseCase(product);
        if (!best) return '<span class="ggcp-fit-note">Not enough data</span>';
        return `<div class="ggcp-score-inline"><strong>${escapeHTML(best.label)}</strong><span class="ggcp-fit-note">Best calculated fit based on available specifications.</span></div>`;
      }

      function renderTable() {
        emptyEl.style.display = selected.length ? 'none' : 'grid';
        tableEl.style.display = selected.length ? 'block' : 'none';
        if (!selected.length) {
          tableEl.innerHTML = '';
          root.classList.remove('ggcp-mobile-specs-expanded');
          return;
        }

        const comparableSpecs = getComparableSpecRows();
        const specRows = comparableSpecs.map((specRow) => ({
          label: specRow.label,
          canonical: specRow.canonical,
          render: (product) => renderComparableSpecCellHTML(specRow, product),
          insights: specRow.canonical === 'weight'
            ? buildWeightInsights(specRow)
            : buildMetricInsights(specRow.canonical || specRow.label, (product) => specRow.valuesByProductId.get(String(product.id)))
        }));

        let rows = [
          {
            label: 'Product',
            render: (product) => `<div class="ggcp-table-product">${imageHTML(product)}<strong>${titleHTML(product)}</strong><a class="ggcp-product-link" href="${escapeHTML(product.url)}">View product</a></div>`
          },
          { label: `${getUseCaseConfig(activeUseCase).label} Index`, render: (product) => renderUseCaseIndexHTML(product) },
          { label: 'Best Use Case', render: (product) => renderBestUseCaseHTML(product) },
          { label: 'Price', render: (product) => `<span class="ggcp-price-reference"><span>${escapeHTML(product.price || 'View price')}</span></span>` },
          ...specRows
        ];

        const dimensionRowIndex = rows.findIndex((row) => row.canonical === 'dimensions');
        if (dimensionRowIndex > -1 && selected.length >= 2) {
          const hasDimensionComparison = selected.filter((product) => parseDimensionInches(getFirstSpecValue(product, ['dimensions']))).length >= 2;
          if (hasDimensionComparison) {
            rows.splice(dimensionRowIndex + 1, 0, {
              label: 'Size Comparison',
              fullWidth: true,
              render: () => renderSizeComparisonHTML(selected)
            });
          }
        }

        root.classList.toggle('ggcp-mobile-specs-expanded', mobileSpecsExpanded);

        if (!specRows.length) {
          tableEl.innerHTML = `<div class="ggcp-no-specs">No product specifications were found. Confirm the products have data in <strong>custom.product_specifications</strong>.</div>`;
          return;
        }

        const visibleRows = window.matchMedia('(max-width: 680px)').matches && !mobileSpecsExpanded ? rows.slice(0, 10) : rows;

        tableEl.innerHTML = `
          <table class="ggcp-table ggcp-table--${selected.length}">
            <colgroup><col class="ggcp-col-feature">${selected.map(() => '<col class="ggcp-col-product">').join('')}</colgroup>
            <thead><tr><th>Feature</th>${selected.map((product) => `<th>${titleHTML(product)}</th>`).join('')}</tr></thead>
            <tbody>
              ${visibleRows.map((row) => {
                if (row.fullWidth) {
                  return `
                    <tr class="ggcp-row--full ggcp-row--size-compare">
                      <td class="ggcp-row-label">${rowLabelHTML(row)}</td>
                      <td class="ggcp-full-cell" colspan="${selected.length}" data-label="${escapeHTML(row.label)}">${row.render()}</td>
                    </tr>
                  `;
                }
                return `
                  <tr class="${row.insights ? 'ggcp-row--scored' : ''}">
                    <td class="ggcp-row-label">${rowLabelHTML(row)}</td>
                    ${selected.map((product) => {
                      const insight = row.insights ? row.insights.byProductId.get(String(product.id)) : null;
                      const baseHtml = row.render(product);
                      return `<td class="${insight && insight.isBest ? 'ggcp-best-cell' : ''}" data-label="${escapeHTML(row.label)}" data-product-title="${escapeHTML(product.shortTitle || product.title)}" data-product-title-mobile="${escapeHTML(product.shortTitle || product.title)}">${renderInsightCell(baseHtml, insight)}</td>`;
                    }).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          ${rows.length > 10 ? `<div class="ggcp-mobile-spec-toggle-wrap"><button class="ggcp-mobile-spec-toggle" type="button" data-gg-mobile-spec-toggle>${mobileSpecsExpanded ? 'Show fewer specs' : `Show all specs (${rows.length})`}</button></div>` : ''}
        `;
        scheduleDetailRowSync();
      }

      function syncDetailRows() {
        const table = tableEl.querySelector('.ggcp-table');
        if (!table) return;
        const rows = Array.from(table.querySelectorAll('tbody tr'));
        rows.forEach((row) => {
          const stacks = Array.from(row.querySelectorAll('.ggcp-value-stack--with-details'));
          if (!stacks.length) return;

          stacks.forEach((stack) => {
            stack.style.removeProperty('--ggcp-detail-offset');
            const spec = stack.querySelector(':scope > .ggcp-spec-main > .ggcp-spec-value');
            const explain = stack.querySelector(':scope > .ggcp-spec-main > .ggcp-explain-note');
            const reserve = stack.querySelector(':scope > .ggcp-score-reserve');
            if (spec) spec.style.minHeight = '';
            if (explain) explain.style.minHeight = '';
            if (reserve) reserve.style.minHeight = '';
          });

          let specMax = 0;
          let explainMax = 0;
          let reserveMax = 0;
          stacks.forEach((stack) => {
            const spec = stack.querySelector(':scope > .ggcp-spec-main > .ggcp-spec-value');
            const explain = stack.querySelector(':scope > .ggcp-spec-main > .ggcp-explain-note');
            const reserve = stack.querySelector(':scope > .ggcp-score-reserve');
            if (spec) specMax = Math.max(specMax, Math.ceil(spec.getBoundingClientRect().height));
            if (explain) explainMax = Math.max(explainMax, Math.ceil(explain.getBoundingClientRect().height));
            if (reserve) reserveMax = Math.max(reserveMax, Math.ceil(reserve.getBoundingClientRect().height));
          });

          stacks.forEach((stack) => {
            const spec = stack.querySelector(':scope > .ggcp-spec-main > .ggcp-spec-value');
            const explain = stack.querySelector(':scope > .ggcp-spec-main > .ggcp-explain-note');
            const reserve = stack.querySelector(':scope > .ggcp-score-reserve');
            if (spec && specMax) spec.style.minHeight = `${specMax}px`;
            if (explain && explainMax) explain.style.minHeight = `${explainMax}px`;
            if (reserve && reserveMax) reserve.style.minHeight = `${reserveMax}px`;
          });

          let maxDetailTop = 0;
          stacks.forEach((stack) => {
            const details = stack.querySelector(':scope > details.ggcp-inline-estimate');
            if (!details) return;
            maxDetailTop = Math.max(maxDetailTop, Math.ceil(details.offsetTop));
          });

          stacks.forEach((stack) => {
            const details = stack.querySelector(':scope > details.ggcp-inline-estimate');
            if (!details) return;
            const offset = Math.max(0, maxDetailTop - Math.ceil(details.offsetTop));
            stack.style.setProperty('--ggcp-detail-offset', `${offset}px`);
          });
        });
      }

      function scheduleDetailRowSync() {
        window.requestAnimationFrame(() => {
          syncDetailRows();
          window.requestAnimationFrame(syncDetailRows);
        });
      }

      function renderAll() {
        renderChips();
        renderResults();
        renderSelected();
        renderPickerState();
        renderUseCasePanel();
        renderTable();
        requestTrayBoundaryCheck();
      }

      function requestTrayBoundaryCheck() {
        if (trayEl) trayEl.classList.remove('is-after-usecase');
      }

      chipsEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-group]');
        if (!button) return;
        const group = button.getAttribute('data-group');
        if (selected.length && group !== selected[0].group) {
          setMessage(`Comparison is locked to ${selected[0].group}. Clear the comparison to choose another category.`, 'warning');
          setStatus('Category locked', `Clear the comparison to choose ${group}.`, 'warning');
          return;
        }
        activeGroup = group;
        input.value = '';
        renderAll();
        input.focus({ preventScroll: true });
      });

      resultsEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-add]');
        if (!button) return;
        addProduct(button.getAttribute('data-add'));
      });

      selectedEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove]');
        if (!button) return;
        removeProduct(button.getAttribute('data-remove'));
      });

      if (useCaseTabsEl) {
        useCaseTabsEl.addEventListener('click', (event) => {
          const button = event.target.closest('[data-usecase]');
          if (!button) return;
          const nextUseCase = button.getAttribute('data-usecase');
          if (!USE_CASES[nextUseCase]) return;
          preserveScroll(() => {
            activeUseCase = nextUseCase;
            renderUseCasePanel();
            renderTable();
          });
        });
      }

      tableEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-gg-mobile-spec-toggle]');
        if (!button) return;
        mobileSpecsExpanded = !mobileSpecsExpanded;
        renderTable();
      });

      if (pickerToggleButton) {
        pickerToggleButton.addEventListener('click', () => {
          if (selected.length < 2) return;
          preserveScroll(() => {
            pickerOpen = !pickerOpen;
            renderAll();
          });
        });
      }

      input.addEventListener('input', renderResults);
      input.addEventListener('keyup', renderResults);
      input.addEventListener('search', renderResults);
      input.addEventListener('focus', renderResults);
      clearButtons.forEach((button) => button.addEventListener('click', clearComparison));
      window.addEventListener('resize', scheduleDetailRowSync, { passive: true });
      window.addEventListener('scroll', requestTrayBoundaryCheck, { passive: true });
      window.addEventListener('resize', requestTrayBoundaryCheck);

      restoreComparisonFromUrl();
      renderAll();
      updateShareUrl();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
